/**
 * Onder welke regels deze woning valt, en hoe streng het platform daarop handelt.
 *
 * Dit scherm gaat over een keuze met rechtsgevolgen, en dat bepaalt de vorm:
 *
 * 1. **Wat er openstaat, staat boven de instellingen.** Wie hier komt omdat een
 *    boeking is geweigerd, hoort niet eerst langs een formulier te moeten.
 * 2. **Elke bevinding noemt zijn bron.** De verordening, het artikel en de datum
 *    waarop het platform het heeft nagekeken. Zonder bron is een waarschuwing een
 *    bewering, en dan is ze niet te weerleggen — precies wat een verhuurder nodig
 *    heeft die meent dat de regel niet op zijn pand slaat (§17.15).
 * 3. **Handhaving verlagen gaat niet per ongeluk.** De verklaring en de erkenning
 *    verschijnen zodra er iets anders dan "afdwingen" wordt gekozen, en het
 *    formulier laat zich niet versturen zonder allebei. Dat is dezelfde eis die de
 *    database afdwingt; hier staat hij zodat de verhuurder leest waar hij mee
 *    instemt in plaats van tegen een foutmelding aan te lopen (ADR-089).
 */
import { useCallback, useEffect, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'

import { useLocale, useTranslate } from '../lib/i18n-provider'
import { listResidences } from '../lib/media-client'
import type { ResidenceOption } from '../lib/media-client'
import {
  REGULATION_MODES,
  acknowledgeFinding,
  complianceErrorMessageKey,
  fetchCompliance,
  listAreas,
  nightsRemaining,
  saveChoice,
} from '../lib/compliance-client'
import type {
  ComplianceFailure,
  ComplianceFinding,
  ComplianceOverview,
  RegulationArea,
  RegulationMode,
} from '../lib/compliance-client'

type State =
  | { readonly phase: 'loading' }
  | { readonly phase: 'ready'; readonly overview: ComplianceOverview }
  | { readonly phase: 'failed'; readonly error: ComplianceFailure }

function FailureNotice({ error }: { readonly error: ComplianceFailure }): ReactElement {
  const t = useTranslate()
  return (
    <div className="notice notice--error" role="alert">
      <p className="notice__title">{t(complianceErrorMessageKey(error))}</p>
      <code className="notice__detail">{error.message}</code>
    </div>
  )
}

export function CompliancePage(): ReactElement {
  const t = useTranslate()
  const { locale } = useLocale()

  const [residences, setResidences] = useState<readonly ResidenceOption[]>([])
  const [residenceId, setResidenceId] = useState<string | null>(null)
  const [areas, setAreas] = useState<readonly RegulationArea[]>([])
  const [state, setState] = useState<State>({ phase: 'loading' })
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<ComplianceFailure | null>(null)

  useEffect(() => {
    let cancelled = false
    void Promise.all([listResidences(), listAreas(locale)]).then(([list, areaList]) => {
      if (cancelled) {
        return
      }
      if (list.ok) {
        setResidences(list.data)
        const first = list.data[0]
        setResidenceId(first === undefined ? null : first.id)
      }
      if (areaList.ok) {
        setAreas(areaList.data)
      }
    })
    return () => {
      cancelled = true
    }
  }, [locale])

  const load = useCallback(async (): Promise<void> => {
    if (residenceId === null) {
      return
    }
    const result = await fetchCompliance(residenceId, locale)
    setState(
      result.ok
        ? { phase: 'ready', overview: result.data }
        : { phase: 'failed', error: result.error },
    )
  }, [residenceId, locale])

  useEffect(() => {
    if (residenceId === null) {
      return
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState({ phase: 'loading' })
    void load()
  }, [residenceId, load])

  const run = useCallback(
    async (work: () => Promise<{ ok: boolean; error?: ComplianceFailure }>): Promise<void> => {
      setBusy(true)
      setActionError(null)
      const result = await work()
      if (!result.ok && result.error !== undefined) {
        setActionError(result.error)
      }
      await load()
      setBusy(false)
    },
    [load],
  )

  return (
    <section className="page" aria-labelledby="compliance-heading">
      <div className="page__header">
        <h1 className="page__title" id="compliance-heading">
          {t('dashboard.compliance.title')}
        </h1>
        <button
          className="button"
          disabled={residenceId === null || busy}
          onClick={() => {
            setState({ phase: 'loading' })
            void load()
          }}
          type="button"
        >
          {t('common.action.reload')}
        </button>
      </div>

      <p className="prose">{t('dashboard.compliance.intro')}</p>

      {residences.length > 1 && (
        <label className="field field--inline">
          <span className="field__label">{t('dashboard.media.residence')}</span>
          <select
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              setResidenceId(event.target.value)
            }}
            value={residenceId ?? ''}
          >
            {residences.map((residence) => (
              <option key={residence.id} value={residence.id}>
                {residence.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {state.phase === 'loading' && (
        <p className="page__status" role="status">
          {t('common.state.loading')}
        </p>
      )}
      {state.phase === 'failed' && <FailureNotice error={state.error} />}
      {actionError !== null && <FailureNotice error={actionError} />}

      {state.phase === 'ready' && residenceId !== null && (
        <>
          <FindingList
            busy={busy}
            findings={state.overview.findings}
            onAcknowledge={(id) => {
              void run(() => acknowledgeFinding(id))
            }}
          />

          <ChoiceForm
            areas={areas}
            busy={busy}
            key={residenceId}
            onSave={(input) => {
              void run(() => saveChoice(residenceId, input))
            }}
            overview={state.overview}
          />
        </>
      )}
    </section>
  )
}

function FindingList({
  findings,
  busy,
  onAcknowledge,
}: {
  readonly findings: readonly ComplianceFinding[]
  readonly busy: boolean
  readonly onAcknowledge: (id: string) => void
}): ReactElement {
  const t = useTranslate()

  if (findings.length === 0) {
    return (
      <p className="page__status" role="status">
        {t('dashboard.compliance.findings.empty')}
      </p>
    )
  }

  return (
    <>
      <h2 className="page__sectionTitle">{t('dashboard.compliance.findings.title')}</h2>
      <ul className="finding-list">
        {findings.map((finding) => {
          const remaining = nightsRemaining(finding)
          return (
            <li className="finding" data-level={finding.level} key={finding.id}>
              <p className="finding__head">
                <span className="finding__level">
                  {t(`dashboard.compliance.level.${finding.level}`)}
                </span>
                {/* De titel komt uit de regelteksten in de database (§17.8); is er
                    geen regel, dan beschrijft de soort de bevinding. */}
                <span className="finding__title">
                  {finding.title !== ''
                    ? finding.title
                    : t(`dashboard.compliance.finding.${finding.type}`)}
                </span>
              </p>

              {finding.summary !== '' && <p className="finding__summary">{finding.summary}</p>}

              {remaining !== null && (
                <p className="finding__numbers">
                  {t('dashboard.compliance.findings.nights', {
                    used: finding.nightsUsed ?? 0,
                    limit: finding.limitNights ?? 0,
                    remaining,
                  })}
                </p>
              )}

              {finding.actionText !== '' && <p className="finding__action">{finding.actionText}</p>}

              {/* Zonder bron is een waarschuwing een bewering (§17.28). */}
              {finding.sourceCitation !== null && (
                <p className="finding__source">
                  {finding.sourceCitation}
                  {finding.lastCheckedOn !== null && (
                    <span className="finding__checked">
                      {t('dashboard.compliance.findings.checked', {
                        date: finding.lastCheckedOn,
                      })}
                    </span>
                  )}
                  {finding.sourceUrl !== null && (
                    <>
                      {' '}
                      <a href={finding.sourceUrl} rel="noreferrer" target="_blank">
                        {t('dashboard.compliance.findings.source')}
                      </a>
                    </>
                  )}
                </p>
              )}

              {finding.status === 'open' && (
                <button
                  className="button button--quiet"
                  disabled={busy}
                  onClick={() => {
                    onAcknowledge(finding.id)
                  }}
                  type="button"
                >
                  {t('dashboard.compliance.findings.acknowledge')}
                </button>
              )}
              {finding.status === 'acknowledged' && (
                <span className="finding__seen">{t('dashboard.compliance.findings.seen')}</span>
              )}
            </li>
          )
        })}
      </ul>
    </>
  )
}

function ChoiceForm({
  overview,
  areas,
  busy,
  onSave,
}: {
  readonly overview: ComplianceOverview
  readonly areas: readonly RegulationArea[]
  readonly busy: boolean
  readonly onSave: (input: {
    areaId: string | null
    mode: RegulationMode
    declaration: string
    registrationNumber: string
    acceptDisclaimer: boolean
  }) => void
}): ReactElement {
  const t = useTranslate()
  const { choice } = overview

  const [areaId, setAreaId] = useState(choice.areaId ?? choice.suggestedAreaId ?? '')
  const [mode, setMode] = useState<RegulationMode>(choice.mode ?? 'enforced')
  const [declaration, setDeclaration] = useState(choice.declaration)
  const [registrationNumber, setRegistrationNumber] = useState(choice.registrationNumber)
  // Een eerder gegeven erkenning telt alleen wanneer hij bij de huidige tekst hoort;
  // wijzigt de tekst, dan moet er opnieuw worden ingestemd (ADR-100).
  const [accepted, setAccepted] = useState(
    choice.disclaimerAcceptedAt !== null &&
      choice.disclaimerVersion === choice.currentDisclaimerVersion,
  )

  const relaxing = mode !== 'enforced'

  function submit(event: FormEvent): void {
    event.preventDefault()
    onSave({
      areaId: areaId === '' ? null : areaId,
      mode,
      declaration: declaration.trim(),
      registrationNumber: registrationNumber.trim(),
      acceptDisclaimer: accepted,
    })
  }

  return (
    <form className="choice-form" onSubmit={submit}>
      <h2 className="page__sectionTitle">{t('dashboard.compliance.choice.title')}</h2>

      <label className="field">
        <span className="field__label">{t('dashboard.compliance.field.area')}</span>
        <select
          className="field__input"
          disabled={busy}
          onChange={(event) => {
            setAreaId(event.target.value)
          }}
          value={areaId}
        >
          <option value="">{t('dashboard.compliance.field.areaNone')}</option>
          {areas.map((area) => (
            <option key={area.id} value={area.id}>
              {/* Naam uit de database, in de taal van de gebruiker: data, geen
                  interfacetekst (§17.8). */}
              {area.name} ({area.code})
            </option>
          ))}
        </select>
        {choice.suggestedAreaId !== null && choice.areaId === null && (
          <span className="field__hint">{t('dashboard.compliance.field.areaSuggested')}</span>
        )}
      </label>

      <fieldset className="choice-form__modes">
        <legend className="field__label">{t('dashboard.compliance.field.mode')}</legend>
        {REGULATION_MODES.map((candidate) => (
          <label className="choice-form__mode" key={candidate}>
            <input
              checked={mode === candidate}
              disabled={busy}
              name="regulation-mode"
              onChange={() => {
                setMode(candidate)
              }}
              type="radio"
              value={candidate}
            />
            <span>
              <strong>{t(`dashboard.compliance.mode.${candidate}`)}</strong>
              <span className="choice-form__modeHint">
                {t(`dashboard.compliance.mode.${candidate}.explanation`)}
              </span>
            </span>
          </label>
        ))}
        {/* Geldt in alle drie de standen; wie dat niet weet, denkt dat uitzetten ook
            het tellen stopzet (ADR-043). */}
        <p className="field__hint">{t('dashboard.compliance.field.alwaysCounts')}</p>
      </fieldset>

      <label className="field">
        <span className="field__label">{t('dashboard.compliance.field.registration')}</span>
        <input
          className="field__input"
          disabled={busy}
          maxLength={100}
          onChange={(event) => {
            setRegistrationNumber(event.target.value)
          }}
          type="text"
          value={registrationNumber}
        />
      </label>

      {relaxing && (
        <div className="choice-form__relax">
          <label className="field">
            <span className="field__label">{t('dashboard.compliance.field.declaration')}</span>
            <textarea
              className="field__input"
              disabled={busy}
              maxLength={4000}
              onChange={(event) => {
                setDeclaration(event.target.value)
              }}
              placeholder={t('dashboard.compliance.field.declarationPlaceholder')}
              rows={4}
              value={declaration}
            />
          </label>

          <label className="choice-form__accept">
            <input
              checked={accepted}
              disabled={busy}
              onChange={(event) => {
                setAccepted(event.target.checked)
              }}
              type="checkbox"
            />
            <span>{t('dashboard.compliance.field.disclaimer')}</span>
          </label>
        </div>
      )}

      <button
        className="button button--primary"
        disabled={busy || (relaxing && (!accepted || declaration.trim().length < 10))}
        type="submit"
      >
        {t('dashboard.compliance.choice.save')}
      </button>
    </form>
  )
}
